import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useAppDispatch, useAppSelector } from "@store";
import { closeDialog, openDialog } from "@store/dialogs";
import { handleCustomColor } from "@store/story";
import { getCustomColors } from "@store/story/selectors";
import { IconDropletOff, IconEdit } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ColorPicker from "@/components/color-picker";
import { isGradient } from "@/components/color-picker/lib/detect";
import { isSameColor } from "@/components/color-picker/lib/equals";
import Tooltip from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";
import { getDesignSystemVariable } from "@/shared";
import {
	AddColorButton,
	ColorSwatch,
	swatchShadow,
} from "./color-swatches";

const THEME_COLORS: { key: string; tooltip: string }[] = [
	{ key: "var(--theme-color-1)", tooltip: "Theme color #1" },
	{ key: "var(--theme-color-2)", tooltip: "Theme color #2" },
	{ key: "var(--theme-color-3)", tooltip: "Theme color #3" },
	{ key: "var(--theme-color-4)", tooltip: "Theme color #4" },
	{ key: "var(--background-main-color)", tooltip: "Background color #1" },
	{ key: "var(--background-secondary-color)", tooltip: "Background color #2" },
];

// The palette also renders inside the story-settings dialog, which would
// otherwise sit on top of the design-system dialog. Closing by id is a no-op
// for every other host (canvas element colors, watermark, etc.).
const STORY_SETTINGS_DIALOG_ID = "story-settings-dialog";

const ColorSelect = ({
	onColorSelect,
	onColorPreview,
	current,
	extraThemeColors = [],
	disableGradient,
	container,
	customColors: customColorsOverride,
	onAddCustomColor,
	onRemoveCustomColor,
	onUpdateCustomColor,
	variablesSection,
}: {
	/**
	 * Final, committed selection — a theme/custom swatch click, "Transparent",
	 * or the picker's "Use color" / "Add gradient" button. Consumers that close
	 * a popover should do so here.
	 */
	onColorSelect: (color: string) => void;
	/**
	 * Transient preview — live picker drags and "Go back" reverts. Consumers
	 * that close on selection should NOT close here. When omitted, transient
	 * events fall back to `onColorSelect` so direct consumers (e.g. watermark)
	 * keep their apply-as-you-drag behaviour.
	 */
	onColorPreview?: (color: string) => void;
	current?: string;
	extraThemeColors?: { key: string; tooltip: string }[];
	disableGradient?: boolean;
	/**
	 * Optional element to resolve CSS `var(...)` against. Supplied by
	 * `ColorsPopover` when `scopedToDesignSystem` is set so tooltip hex
	 * values reflect the theme being edited, not the applied story theme.
	 */
	container?: HTMLElement | null;
	/**
	 * Optional storage override for custom colors. When provided, the
	 * palette reads from `customColors` and writes via the provided
	 * handlers instead of dispatching to Redux. Used by the design-system
	 * dialog so staged edits flow through Zustand and don't write to
	 * persisted story state until Save Changes.
	 */
	customColors?: string[];
	onAddCustomColor?: (color: string) => void;
	onRemoveCustomColor?: (index: number) => void;
	onUpdateCustomColor?: (index: number, color: string) => void;
	/**
	 * Optional extra section rendered between "Custom colors" and "Gradients" —
	 * the same slot the settings-panel `Color` control puts its
	 * `ColorVariablesSection` in, so the palette reads identically wherever it
	 * is hosted. Supplied by `ColorsPopover`; omitted (and therefore absent) for
	 * direct consumers such as the watermark panel.
	 */
	variablesSection?: React.ReactNode;
}) => {
	const [parent] = useAutoAnimate();
	// Transient events (live drag, revert, entering edit) route here so closing
	// consumers don't commit/close mid-edit. Falls back to onColorSelect.
	const previewColor = useCallback(
		(color: string) => (onColorPreview ?? onColorSelect)(color),
		[onColorPreview, onColorSelect],
	);
	const [pickerMode, setPickerMode] = useState<null | "solid" | "gradient">(
		null,
	);
	const valueBeforePickerRef = useRef<string>(current ?? "");
	const pickerModeRef = useRef(pickerMode);
	pickerModeRef.current = pickerMode;
	const gradientCommittedRef = useRef(false);
	const editingIndexRef = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (
				pickerModeRef.current === "gradient" &&
				!gradientCommittedRef.current
			) {
				previewColor(valueBeforePickerRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const reduxColors = useAppSelector(getCustomColors);
	const dispatch = useAppDispatch();
	const colors = customColorsOverride ?? reduxColors;
	const useOverride = customColorsOverride !== undefined;
	const handleAddCustomColor = useCallback(
		(color: string) => {
			gradientCommittedRef.current = true;
			if (editingIndexRef.current !== null) {
				if (useOverride) {
					onUpdateCustomColor?.(editingIndexRef.current, color);
				} else {
					dispatch(
						handleCustomColor({
							color,
							index: editingIndexRef.current,
							action: "update",
						}),
					);
				}
				editingIndexRef.current = null;
			} else if (useOverride) {
				onAddCustomColor?.(color);
			} else {
				dispatch(handleCustomColor({ color, action: "add" }));
			}
			onColorSelect(color);
			setPickerMode(null);
		},
		[
			dispatch,
			onColorSelect,
			useOverride,
			onAddCustomColor,
			onUpdateCustomColor,
		],
	);
	const handleRemoveCustomColor = useCallback(
		(index: number) => {
			if (useOverride) {
				onRemoveCustomColor?.(index);
			} else {
				dispatch(handleCustomColor({ index, action: "remove" }));
			}
		},
		[dispatch, useOverride, onRemoveCustomColor],
	);

	const themeColors = useMemo(
		() => [...extraThemeColors, ...THEME_COLORS],
		[extraThemeColors],
	);

	return (
		<div
			ref={parent}
			className="tw-z-[99999] tw-rounded-xl tw-bg-white tw-outline tw-outline-1 tw-outline-[rgba(18,3,62,0.05)]"
			style={{
				boxShadow:
					"0px 5px 2px rgba(10,10,10,0.01), 0px 3px 2px rgba(10,10,10,0.03), 0px 1px 1px rgba(10,10,10,0.04), 0px 0px 1px rgba(10,10,10,0.05)",
			}}
		>
			{pickerMode !== null ? (
				<div key="color-picker">
					<ColorPicker
						current={getDesignSystemVariable(current ?? "", container)}
						onColorChange={previewColor}
						onColorAdd={handleAddCustomColor}
						onGoBack={() => {
							previewColor(valueBeforePickerRef.current);
							setPickerMode(null);
						}}
						disableGradient={pickerMode === "solid"}
						initialMode={pickerMode}
						customSolidColors={(colors ?? []).filter((c) => !isGradient(c))}
						isEditing={editingIndexRef.current !== null}
					/>
				</div>
			) : (
				<ThemeColorsOptions
					key="color-list"
					current={current}
					themeColors={themeColors}
					onCustomColorRemove={handleRemoveCustomColor}
					setPickerMode={(mode, editColor, editIndex) => {
						editingIndexRef.current = editIndex ?? null;
						valueBeforePickerRef.current = current ?? "";
						gradientCommittedRef.current = false;
						if (editColor) previewColor(editColor);
						setPickerMode(mode);
					}}
					colors={colors ?? []}
					onColorSelect={onColorSelect}
					disableGradient={disableGradient}
					container={container}
					variablesSection={variablesSection}
				/>
			)}
		</div>
	);
};

const ThemeColorsOptions = ({
	themeColors,
	onCustomColorRemove,
	current,
	setPickerMode,
	colors,
	onColorSelect,
	disableGradient,
	container,
	variablesSection,
}: {
	themeColors: { key: string; tooltip: string }[];
	current?: string;
	colors: string[];
	onCustomColorRemove: (index: number) => void;
	setPickerMode: (
		mode: "solid" | "gradient",
		editColor?: string,
		editIndex?: number,
	) => void;
	onColorSelect: (color: string) => void;
	disableGradient?: boolean;
	container?: HTMLElement | null;
	variablesSection?: React.ReactNode;
}) => {
	const solidColors = useMemo(
		() =>
			colors
				.map((c, originalIndex) => ({ color: c, originalIndex }))
				.filter(({ color }) => !isGradient(color)),
		[colors],
	);

	const gradientColors = useMemo(
		() =>
			colors
				.map((c, originalIndex) => ({ color: c, originalIndex }))
				.filter(({ color }) => isGradient(color)),
		[colors],
	);

	const dispatch = useAppDispatch();

	// `design-system/**` imports this palette, so the dialog is imported lazily
	// here — a static import back would close an ESM cycle.
	const handleDesignSystem = useCallback(async () => {
		const { default: DesignSystemDialog } = await import(
			"@pages/editor/modules/design-system"
		);
		dispatch(closeDialog(STORY_SETTINGS_DIALOG_ID));
		dispatch(
			openDialog({
				id: DesignSystemDialog.id,
				content: DesignSystemDialog,
			}),
		);
	}, [dispatch]);

	return (
		<div className="tw-flex tw-w-[264px] tw-flex-col tw-gap-4 tw-p-4">
			{/* ── Theme colors ── */}
			<div className="tw-flex tw-flex-col tw-gap-2 tw-py-1">
				<div className="tw-flex tw-h-8 tw-items-center tw-justify-between">
					<span className="tw-font-sans tw-text-sm tw-font-medium tw-leading-5 tw-text-[#12033E]">
						Theme colors
					</span>
					<button
						type="button"
						onClick={() => void handleDesignSystem()}
						className="tw-flex tw-h-8 tw-cursor-pointer tw-items-center tw-gap-0.5 tw-rounded-lg tw-px-2 tw-transition-opacity tw-duration-200 tw-ease-in-out hover:tw-opacity-70"
					>
						<IconEdit className="tw-size-3.5 tw-text-[#610CFF]" />
						<span className="tw-font-sans tw-text-xs tw-font-medium tw-leading-4 tw-text-[#610CFF]">
							Edit
						</span>
					</button>
				</div>
				<div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-gap-2">
					{themeColors.map(({ key, tooltip }) => (
						<div key={key}>
							<Tooltip placement="top">
								<Tooltip.Trigger>
									<div
										role="button"
										tabIndex={0}
										onClick={() => onColorSelect(key)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ")
												onColorSelect(key);
										}}
										style={{
											background: key,
											...swatchShadow(isSameColor(current, key)),
										}}
										className={cn(
											"tw-h-8 tw-w-8 tw-cursor-pointer tw-rounded-full tw-border-none tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-blue-base/10 tw-transition-[box-shadow,transform] tw-duration-200 tw-ease-in-out hover:tw-scale-[1.0625]",
										)}
									/>
								</Tooltip.Trigger>
								<Tooltip.Content className="tw-z-[9999]">
									<Tooltip.Text>
										{tooltip}: {getDesignSystemVariable(key, container)}
									</Tooltip.Text>
								</Tooltip.Content>
							</Tooltip>
						</div>
					))}
				</div>
			</div>

			{/* ── Custom colors (solids only) ── */}
			<div className="tw-flex tw-flex-col tw-gap-2 tw-py-1">
				<span className="tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-[#12033E]">
					Custom colors
				</span>
				<div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-gap-2">
					<AddColorButton
						onClick={() => setPickerMode("solid")}
						tooltip="Add color"
					/>
					{solidColors.map(({ color, originalIndex }) => (
						<ColorSwatch
							key={originalIndex}
							color={color}
							isActive={isSameColor(current, color)}
							onSelect={() => onColorSelect(color)}
							onEdit={() => setPickerMode("solid", color, originalIndex)}
							onRemove={() => onCustomColorRemove(originalIndex)}
							tooltipText={`Color: ${color}`}
						/>
					))}
				</div>
			</div>

			{/* ── Variables (color-type dynamic variables) ── */}
			{variablesSection}

			{/* ── Gradients ── */}
			{!disableGradient && (
				<div className="tw-flex tw-flex-col tw-gap-2 tw-py-1">
					<span className="tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-[#12033E]">
						Gradients
					</span>
					<div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-gap-2">
						<AddColorButton
							onClick={() => setPickerMode("gradient")}
							tooltip="Add gradient"
						/>
						{gradientColors.map(({ color, originalIndex }) => (
							<ColorSwatch
								key={originalIndex}
								color={color}
								isActive={isSameColor(current, color)}
								onSelect={() => onColorSelect(color)}
								onEdit={() => setPickerMode("gradient", color, originalIndex)}
								onRemove={() => onCustomColorRemove(originalIndex)}
								tooltipText={`Gradient: ${color}`}
							/>
						))}
					</div>
				</div>
			)}

			{/* ── Transparent ── */}
			<button
				type="button"
				onClick={() => onColorSelect("transparent")}
				className="tw-flex tw-h-8 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-0.5 tw-rounded-lg tw-border-none tw-bg-white tw-font-sans tw-text-xs tw-font-medium tw-leading-4 tw-text-[#12033E] tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue-base/5 tw-transition-colors hover:tw-bg-blue-50"
			>
				<IconDropletOff className="tw-size-3.5 tw-text-[#12033E]" />
				<span>Transparent</span>
			</button>
		</div>
	);
};

export default ColorSelect;
